import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { MessageSquare, Send, X, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import HelpDot from '@/components/ui/HelpDot';
import { apiFetch } from '@/lib/api';
import { chatInstructAtom } from '@/hooks/atoms';
import type { ChatMessage } from '@/hooks/useProduction';

interface ChatPanelProps {
  productionId: string;
  // Live messages streamed via SSE (from useProductionProgress state.chat).
  liveMessages: ChatMessage[];
}

// A proposal in any of these states is settled — its Confirm/Reject buttons are
// gone for good and an SSE replay (always "awaiting_confirm") must not revive them.
const RESOLVED_STATUSES = new Set(['confirmed', 'rejected', 'executing', 'done', 'error']);

const LAUNCHER_CLS =
  'fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-primary ' +
  'px-4 py-3 text-primary-foreground shadow-lg hover:opacity-90';
const PANEL_CLS =
  'fixed bottom-5 right-5 z-40 flex w-[380px] max-w-[calc(100vw-2.5rem)] flex-col ' +
  'rounded-lg border border-border bg-card shadow-2xl';
const INPUT_CLS =
  'flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 ' +
  'text-sm focus:outline-none focus:ring-1 focus:ring-ring';

/**
 * Live Director chat — the producer converses with the running production in real time.
 * Loads history from GET /api/chat/{id}, merges the SSE `chat` stream, sends messages,
 * and renders Director proposals as Confirm / Reject cards (propose-then-confirm).
 */
export default function ChatPanel({ productionId, liveMessages }: ChatPanelProps) {
  const { t } = useTranslation('production');
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [instruct, setInstruct] = useAtom(chatInstructAtom);
  const [pendingRef, setPendingRef] = useState<{ assetRef: string; label: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Load history once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiFetch(`/api/chat/${productionId}`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (cancelled) return;
        const hist: ChatMessage[] = (data.messages || []).map((m: Record<string, unknown>) => ({
          id: m.id as string,
          role: m.role as ChatMessage['role'],
          text: m.text as string,
          kind: (m.kind as ChatMessage['kind']) || 'message',
          action: (m.action as ChatMessage['action']) || null,
          asset_ref: (m.asset_ref as string) || '',
          status: (m.status as string) || 'done',
          timestamp: Date.now(),
        }));
        setMessages(hist);
      } catch {
        // history is best-effort
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productionId]);

  // Merge live SSE messages by id (dedupe against history + prior stream).
  // The SSE stream REPLAYS its whole log on every (re)connect, and a proposal
  // event always carries status "awaiting_confirm" — so a naive merge would keep
  // resurrecting the Confirm/Reject buttons after the user already resolved it.
  // Never downgrade a proposal that is already resolved (locally or per history).
  useEffect(() => {
    if (!liveMessages.length) return;
    setMessages((prev) => {
      const byId = new Map(prev.map((m) => [m.id, m]));
      for (const m of liveMessages) {
        const existing = byId.get(m.id);
        if (existing && RESOLVED_STATUSES.has(existing.status || '') && m.status === 'awaiting_confirm') {
          byId.set(m.id, { ...existing, ...m, status: existing.status });
        } else {
          byId.set(m.id, { ...existing, ...m });
        }
      }
      return Array.from(byId.values());
    });
  }, [liveMessages]);

  // A gallery tile asked to "instruct about this" — open + prime the asset ref.
  useEffect(() => {
    if (instruct) {
      setPendingRef({ assetRef: instruct.assetRef, label: instruct.label });
      setOpen(true);
      setInstruct(null);
    }
  }, [instruct, setInstruct]);

  // Auto-scroll to newest.
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = useCallback(async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const resp = await apiFetch(`/api/chat/${productionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: body, asset_ref: pendingRef?.assetRef || '' }),
      });
      // Render the producer's own message immediately rather than waiting on the SSE
      // echo (which never arrives if the stream isn't actively connected). Use the
      // server's message_id so the later echo dedupes instead of duplicating.
      let mid = `producer-${Date.now()}`;
      try {
        const data = await resp.json();
        if (data?.message_id) mid = data.message_id;
      } catch {
        // ignore — fall back to the temp id
      }
      setMessages((prev) =>
        prev.some((m) => m.id === mid)
          ? prev
          : [
              ...prev,
              {
                id: mid,
                role: 'producer',
                text: body,
                kind: 'message',
                asset_ref: pendingRef?.assetRef || '',
                status: 'done',
                timestamp: Date.now(),
              },
            ],
      );
      setText('');
      setPendingRef(null);
    } catch {
      // leave the text so the user can retry
    } finally {
      setSending(false);
    }
  }, [text, sending, productionId, pendingRef]);

  const confirm = useCallback(
    async (messageId: string, approve: boolean) => {
      // Optimistic: flip the card immediately.
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, status: approve ? 'confirmed' : 'rejected' } : m)));
      try {
        await apiFetch(`/api/chat/${productionId}/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message_id: messageId, approve }),
        });
      } catch {
        // revert on failure
        setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, status: 'awaiting_confirm' } : m)));
      }
    },
    [productionId],
  );

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className={LAUNCHER_CLS}>
        <MessageSquare className="h-5 w-5" />
        <span className="text-sm font-medium">{t('chat.title')}</span>
      </button>
    );
  }

  return (
    <div className={PANEL_CLS}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">{t('chat.panelTitle')}</span>
          <HelpDot>{t('chat.why')}</HelpDot>
        </div>
        <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Thread */}
      <div ref={scrollRef} className="flex max-h-[50vh] min-h-[200px] flex-col gap-2 overflow-y-auto p-3">
        {messages.length === 0 && <p className="px-1 py-6 text-center text-xs text-muted-foreground">{t('chat.empty')}</p>}
        {messages.map((m) => (
          <ChatBubble key={m.id} msg={m} onConfirm={confirm} />
        ))}
      </div>

      {/* Composer */}
      <div className="border-t border-border p-2.5">
        {pendingRef && (
          <div className="mb-1.5 flex items-center gap-1.5 rounded bg-muted px-2 py-1 text-xs">
            <span className="text-muted-foreground">Re:</span>
            <span className="font-mono font-medium truncate">{pendingRef.label}</span>
            <button onClick={() => setPendingRef(null)} className="ml-auto text-muted-foreground hover:text-foreground">
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={t('chat.placeholder')}
            rows={1}
            className={INPUT_CLS}
          />
          <Button size="sm" onClick={send} disabled={sending || !text.trim()}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ msg, onConfirm }: { msg: ChatMessage; onConfirm: (id: string, approve: boolean) => void }) {
  const { t } = useTranslation('production');
  const isProducer = msg.role === 'producer';
  const isProposal = msg.kind === 'proposal';

  return (
    <div className={isProducer ? 'flex justify-end' : 'flex justify-start'}>
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
          isProducer
            ? 'bg-primary text-primary-foreground'
            : msg.kind === 'result'
              ? 'bg-green-500/10 text-foreground border border-green-500/30'
              : 'bg-muted text-foreground'
        }`}
      >
        {!isProducer && (
          <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Director</div>
        )}
        <div className="whitespace-pre-wrap">{msg.text}</div>

        {isProposal && (
          <div className="mt-2 rounded-md border border-border bg-background/60 p-2">
            {msg.action?.summary && <div className="mb-2 text-xs font-medium">{msg.action.summary}</div>}
            {msg.status === 'awaiting_confirm' ? (
              <div className="flex gap-2">
                <Button size="sm" className="h-7 flex-1" onClick={() => onConfirm(msg.id, true)}>
                  <Check className="mr-1 h-3.5 w-3.5" />
                  {t('chat.confirm')}
                </Button>
                <Button size="sm" variant="outline" className="h-7 flex-1" onClick={() => onConfirm(msg.id, false)}>
                  <X className="mr-1 h-3.5 w-3.5" />
                  {t('chat.reject')}
                </Button>
              </div>
            ) : (
              <div className="text-xs font-medium text-muted-foreground">
                {msg.status === 'confirmed' ? t('chat.confirmed') : msg.status === 'rejected' ? t('chat.rejected') : msg.status}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
